import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import { format } from 'date-fns';
import { FiPlus } from 'react-icons/fi';
import { IoIosArrowBack, IoMdDownload } from 'react-icons/io';
import { useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as Yup from 'yup';

import { ButtonBlack, Input, Layout, Select } from '~/components';
import { useDataSheets } from '~/hooks/dataSheets';
import { useScreen } from '~/hooks/screen';
import { useSequency } from '~/hooks/sequency';
import api from '~/services/api';
import getValidationErrors from '~/utils/getValidationErrors';

import {
  DivisionData,
  DivisionDataResults,
} from '../TurboTraining/Basic/TrainingProtocol/PrescriptionScreen/types';
import { Card } from './Card';
import { IDataSheet, IProgram } from './interface';
import Modal from './Modal';

import {
  Billing,
  Container,
  FieldSet,
  FieldSetWrapper,
  FieldTitle,
  FormContainer,
  HeaderButtons,
  Message,
  ModalContainer,
  NewButton,
  NewButtonContainer,
  NewButtonLabel,
  PageHeader,
  PageHeaderBackButton,
  PageHeaderBackButtonLabel,
  Program,
  ProgramInfo,
  RadioInput,
  Section,
  Wrapper,
  RedirectModalContainer,
} from './styles';

type RouteParams = {
  userId: string;
  billingId: string;
  programId: string;
};

export default function TrainingProgram() {
  const { push, replace } = useHistory();
  const { userId: studentId, billingId, programId } = useParams<RouteParams>();
  const { loadDataSheets, dataSheetsList } = useDataSheets();
  const { updateScreen } = useScreen();
  const { updateSequencies } = useSequency();

  const [loading, setLoading] = useState(false);
  const [sheetModelModal, setSheetModelModal] = useState(false);
  const [program, setProgram] = useState<IProgram>();
  const [divisions, setDivisions] = useState<DivisionDataResults[]>([]);

  useEffect(() => {
    program && updateSequencies(program.id);
  }, [program, updateSequencies]);

  useEffect(() => {
    loadDataSheets(studentId);
    updateScreen(`trainingPrograms`);
  }, [loadDataSheets, studentId, updateScreen]);

  const handleGetDivisions = async () => {
    const { data } = await api.get<IProgram[]>(
      `billings/${billingId}/programs/${programId}`,
    );

    const response = await api.get<DivisionData>(
      `training-programs/${programId}/divisions`,
    );

    setProgram(data[0]);
    setDivisions(response.data.results);

    if (response.data.results.length < 1) {
      setSheetModelModal(true);
    }
  };

  // Carrega dados do programa
  useEffect(() => {
    handleGetDivisions();
  }, [billingId, programId]);

  // const handleNavigateToNewTurboTraining = useCallback(() => {
  //   replace(`/professional/students/${studentId}`);

  //   push(
  //     `/professional/students/${studentId}/billing/${billingId}/training-programs/${programId}/turbo-training`,
  //     program,
  //   );
  // }, [replace, push, studentId, billingId, programId, program]);

  // ? Não entendi isso aqui
  // useEffect(() => {
  //   if (program) {
  //     console.log('tem programa');

  //     handleNavigateToNewTurboTraining();
  //   }
  // }, [program, handleNavigateToNewTurboTraining]);

  const handleNavigateToBilling = useCallback(() => {
    push(`/professional/students/${studentId}/billings/${billingId}`);
  }, [push, studentId, billingId]);

  function SheetModelModalView() {
    const formRef = useRef<FormHandles>(null);

    const [sheetModel, setSheetModel] = useState('Basic');
    const [showRedirectModal, setShowRedirectModal] = useState(false);

    const dataSheetsMemo = useMemo(
      () =>
        dataSheetsList.map((dataSheet: IDataSheet) => ({
          value: dataSheet.id,
          label: format(new Date(dataSheet.created_at), 'dd/MM/yyyy'),
        })),
      [],
    );

    useEffect(() => {
      dataSheetsMemo.length === 0 ? setShowRedirectModal(true) : false;
    }, [dataSheetsMemo]);

    const radioSheetModelOptionsList = useMemo(
      () => [
        { value: 'Basic', label: 'Basic' },
        { value: 'Premium', label: 'Premium' },
        { value: 'MasterClass', label: 'MasterClass' },
      ],
      [],
    );

    const RadioSheetModelOptions = useMemo(
      () =>
        radioSheetModelOptionsList.map((item) => (
          <RadioInput
            key={item.label}
            isSelected={item.value === sheetModel}
            onClick={() => setSheetModel(item.value)}
          >
            <div className="radio">
              <div className="filled" />
            </div>

            <p>{item.label}</p>
          </RadioInput>
        )),
      [radioSheetModelOptionsList, sheetModel],
    );

    const selectStyle = {
      placeholder: () => ({
        padding: '0 1.7rem',
        color: '#00000080',
      }),
      singleValue: () => ({
        padding: '0 1rem',
      }),
    };

    const inputStyle = {
      height: 35,
    };

    function RedirectModal() {
      return (
        <Modal
          isOpen={showRedirectModal}
          setOpen={setShowRedirectModal}
          maxHeightStyled="50rem"
          maxWidthStyled="70rem"
        >
          <RedirectModalContainer>
            <h1>Atenção!</h1>

            <div>
              Deve haver uma ficha técnica cadastrada para poder criar um
              treino!
            </div>

            <ButtonBlack
              onClick={() =>
                push(`/professional/students/${studentId}/datasheet/register`)
              }
            >
              OK
            </ButtonBlack>
          </RedirectModalContainer>
        </Modal>
      );
    }

    const handleSubmit = useCallback(
      async (data: FormData) => {
        setLoading(true);

        const formData = {
          ...data,
          data_model: sheetModel,
          billing_id: Number(billingId),
          student_id: Number(studentId),
          program_id: Number(programId),
        };

        const schema = Yup.object().shape({
          datasheet_id: Yup.number().required('O campo é obrigatório!'),
          training_division: Yup.string().required('O campo é obrigatório!'),
          data_model: Yup.string().required('O campo é obrigatório!'),
          billing_id: Yup.number().required('O campo é obrigatório!'),
          student_id: Yup.number().required('O campo é obrigatório!'),
          program_id: Yup.number().required('O campo é obrigatório!'),
        });

        try {
          await schema.validate(formData, { abortEarly: false });

          const { data: apiData } = await api.post(
            '/training-programs',
            formData,
          );

          setProgram(apiData);

          toast.success('Novo programa cadastrado com sucesso!');

          setSheetModelModal(false);

          await handleGetDivisions();
        } catch (err) {
          console.log(err);

          setLoading(false);

          if (err instanceof Yup.ValidationError) {
            const errors = getValidationErrors(err);

            formRef.current?.setErrors(errors);
          }

          toast.error('Erro ao cadastrar novo programa!');
        }
      },
      [sheetModel],
    );

    // const formatingTraningDivisionText = (value: string) => {
    const formatingTraningDivisionText = (
      value: React.ChangeEvent<HTMLInputElement>,
    ) => {
      const formattedValue = value.target.value;
      const txtFaseOne = formattedValue.replace(/,|\d+$|([H-Z])|([h-z])/gm, '');
      const txtFaseTwo = txtFaseOne.split('');
      const txtFaseThree = txtFaseTwo.join();
      const txtFaseFour = txtFaseThree.toUpperCase();

      formRef?.current?.setFieldValue('training_division', txtFaseFour);
    };

    return (
      <Modal
        isOpen={sheetModelModal}
        setOpen={setSheetModelModal}
        maxWidthStyled={621}
        maxHeightStyled={600}
      >
        <RedirectModal />

        <ModalContainer>
          <h1>Modelo de ficha</h1>

          <FieldSet>
            <FieldSetWrapper>{RadioSheetModelOptions}</FieldSetWrapper>
          </FieldSet>

          <FormContainer>
            <Form ref={formRef} onSubmit={handleSubmit}>
              <FieldSet>
                <FieldTitle secondary>Ficha técnica:</FieldTitle>
                <Select
                  name="datasheet_id"
                  placeholder="Selecionar"
                  isSearchable={false}
                  defaultValue={dataSheetsMemo[0]}
                  options={dataSheetsMemo}
                  styles={selectStyle}
                />
              </FieldSet>

              <FieldSet>
                <FieldTitle secondary>
                  Divisão de treino (A, B, C...):
                </FieldTitle>
                <Input
                  name="training_division"
                  placeholder="exemplo: A,B,C..."
                  onChange={formatingTraningDivisionText}
                  style={inputStyle}
                />
              </FieldSet>
            </Form>
          </FormContainer>

          <ButtonBlack
            disabled={loading}
            onClick={() => formRef.current?.submitForm()}
          >
            Selecionar
          </ButtonBlack>
        </ModalContainer>
      </Modal>
    );
  }

  function goBack() {
    push(`/professional/students/${studentId}`);
  }

  return (
    <Layout title="Aluno">
      <SheetModelModalView />

      <PageHeader>
        <Wrapper>
          <PageHeaderBackButton onClick={goBack}>
            <IoIosArrowBack />
            <PageHeaderBackButtonLabel>
              divisões de treino
            </PageHeaderBackButtonLabel>
          </PageHeaderBackButton>

          <ProgramInfo>
            <Billing onClick={handleNavigateToBilling}>
              <strong>Fatura: </strong>
              {billingId}
            </Billing>

            <Program>
              <strong>Programa: </strong>
              {programId}
            </Program>
          </ProgramInfo>
        </Wrapper>

        {/* //TODO Só pode baixar o pdf após o treino já for emitido, e ele irá ficar nos 3 pontinhos do card */}
        {/* <HeaderButtons>
          <button type="button">
            <IoMdDownload color="#000" size="19" />
          </button>
        </HeaderButtons> */}
      </PageHeader>

      <Container>
        {divisions?.length > 0 ? (
          <Section>
            {divisions?.map((division) => {
              return (
                <Card
                  program={division ?? ({} as DivisionDataResults)}
                  studentId={studentId}
                  billingId={billingId}
                  updateData={handleGetDivisions}
                />
              );
            })}
          </Section>
        ) : (
          <>
            <NewButtonContainer>
              <NewButton onClick={() => setSheetModelModal(true)}>
                <FiPlus />
                <NewButtonLabel>novo treino</NewButtonLabel>
              </NewButton>
            </NewButtonContainer>

            <Message>Nenhum treino cadastrado</Message>
          </>
        )}
        )
      </Container>
    </Layout>
  );
}
